import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useCapabilities, useCapability } from '../../../../hooks/use-capability';
import { useConversation } from '../../../../hooks/use-conversation';
import { useMessages } from '../../../../hooks/use-messages';
import { usePresence } from '../../../../hooks/use-presence';
import { api } from '../../../../lib/api-client';
import { useAuthStore } from '../../../../stores/auth-store';
import { isTerminalStatus, relativeTime, statusLabel } from '../../../conversations/format';
import {
  CoAttendantToolsPanel,
  ConversationMirror,
  ForceTakeoverModal,
  ForceTrocaPlaybookModal,
  InvisibleJustificationModal,
  MonitoringModeSelector,
  PageHead,
  SubstituirAtendenteModal,
} from '../../components';
import { useAcompanhamentoWsSync } from '../../hooks/use-acompanhamento-ws-sync';
import {
  useActiveCoAttendance,
  useAvailablePlaybooks,
  useChangeRole,
  useCoAttendanceSession,
  useCoachingMessages,
  useComposerState,
  useDiscrepancy,
  useForceActions,
  useParallelConversations,
  useQualityIndicators,
  useSessionNote,
} from '../../hooks/use-coattendance';

import { adaptMirror, adaptPanelQuality, adaptParallelConv } from './acompanhamento-adapter';

import type { MonitoringMode } from '../../components/badges/SilentMonitoringIndicator';
import type { CoAttendantToolsCoachingMessageVM } from '../../components/cards/CoAttendantToolsPanel';
import type { CoAttendantRole } from '../../types';
import type { PresenceState, SupervisorVariant } from '../../view-models';
import type React from 'react';

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

/** Cada role do co-atendimento tem uma visibilidade implícita (mockup §sceneToComposite). */
const ROLE_VISIBILITY: Record<CoAttendantRole, MonitoringMode> = {
  shadow_observer: 'visible-to-attendant',
  collaborative_observer: 'visible-to-both',
  supervisor_monitor: 'invisible-to-all',
};

function SoloEmpty(): React.JSX.Element {
  return (
    <>
      <PageHead title="Acompanhamento silencioso" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Acompanhamento requer Team-Pro+</h3>
        <p>Observar uma conversa em curso sem interromper é um recurso de operação em equipe.</p>
      </div>
    </>
  );
}

/** Landing/picker (D1) — sem conversationId. Picker REAL, nunca fake. */
function Landing(): React.JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const attendantId = params.get('attendantId') ?? undefined;
  const { data: presence } = usePresence();
  const active = useActiveCoAttendance();

  const nameFor = (userId: string): string => {
    const found = (presence as unknown as { userId: string; name?: string }[]).find(
      (p) => p.userId === userId,
    );
    return found?.name ?? userId;
  };

  // Conversas in_progress do atendente (vindo da Tela 1) — picker real (D1).
  const convQuery = useQuery({
    queryKey: ['acomp-attendant-conversations', attendantId],
    enabled: attendantId !== undefined,
    queryFn: async () => {
      if (attendantId === undefined) return [];
      const res = await api.listConversations({
        assignedToUserId: attendantId,
        status: 'in-progress',
      });
      return res.data;
    },
  });
  const convs = convQuery.data ?? [];

  // A-16 (noturna 04/Set/2026): sem `attendantId` a tela dizia «Selecione uma conversa no
  // Painel da Equipe» — e o Painel não listava a conversa queue-held que mais precisava do
  // supervisor. Lista as conversas em andamento do tenant: humanas (in-progress) + IA dirigindo.
  const activeConvs = useQuery({
    queryKey: ['acomp-active-conversations'],
    enabled: attendantId === undefined,
    staleTime: 15_000,
    queryFn: async () => {
      const [human, ai] = await Promise.all([
        api.listConversations({ status: 'in-progress', sort: 'recency', limit: 20 }),
        api.listConversations({ operationMode: 'H_c', sort: 'recency', limit: 20 }),
      ]);
      const seen = new Set<string>();
      return [...human.data, ...ai.data].filter((c) => {
        if (seen.has(c.id) || isTerminalStatus(c.status)) return false;
        seen.add(c.id);
        return true;
      });
    },
  });

  const open = (conversationId: string): void => {
    navigate(`/supervisor/acompanhamento/${conversationId}`);
  };

  return (
    <>
      <PageHead
        title="Acompanhamento silencioso"
        subtitle={
          attendantId !== undefined
            ? `Conversas ativas de ${nameFor(attendantId)}`
            : 'Selecione uma conversa para acompanhar'
        }
      />
      {attendantId !== undefined ? (
        <div className="acomp-picker">
          {convQuery.isLoading && <p style={{ color: 'var(--fg-3)' }}>Carregando conversas…</p>}
          {!convQuery.isLoading && convs.length === 0 && (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--fg-3)' }}>
              <p>Sem conversas em andamento deste atendente no momento.</p>
            </div>
          )}
          {convs.map((c) => (
            <button
              key={c.id}
              type="button"
              className="acomp-picker__item"
              onClick={() => {
                open(c.id);
              }}
            >
              <b>{c.person?.displayName ?? '—'}</b>
              <span style={{ color: 'var(--fg-3)', marginLeft: 'var(--space-2)' }}>
                {c.channel}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="acomp-picker">
          <p style={{ color: 'var(--fg-3)' }}>Escolha uma conversa em andamento para acompanhar.</p>
          {activeConvs.isLoading && <p style={{ color: 'var(--fg-3)' }}>Carregando conversas…</p>}
          {!activeConvs.isLoading && (activeConvs.data ?? []).length === 0 && (
            <p style={{ color: 'var(--fg-3)' }}>Nenhuma conversa em andamento agora.</p>
          )}
          {(activeConvs.data ?? []).map((c) => (
            <button
              key={c.id}
              type="button"
              className="acomp-picker__item"
              onClick={() => {
                open(c.id);
              }}
            >
              <b>{c.person?.displayName ?? 'Sem identificação'}</b>
              <span style={{ color: 'var(--fg-3)', marginLeft: 'var(--space-2)' }}>
                {c.operationMode.current === 'H_c'
                  ? 'IA dirige'
                  : c.assignedToUserId !== null && c.assignedToUserId !== undefined
                    ? `com ${nameFor(c.assignedToUserId)}`
                    : 'aguardando atendente'}
                {' · '}
                {statusLabel(c.status)}
              </span>
            </button>
          ))}
          {(active.data ?? [])
            .filter((s) => s.removedAt === null)
            .map((s) => (
              <button
                key={s.id}
                type="button"
                className="acomp-picker__item"
                onClick={() => {
                  open(s.conversationId);
                }}
              >
                Voltar à sessão de acompanhamento em andamento
              </button>
            ))}
        </div>
      )}
    </>
  );
}

/** Espelho — conversationId presente. */
function Mirror({
  conversationId,
  variant,
}: {
  conversationId: string;
  variant: SupervisorVariant;
}): React.JSX.Element {
  const navigate = useNavigate();
  const supervisorName = useAuthStore((s) => s.user?.name) ?? 'Supervisora';
  const currentUserId = useAuthStore((s) => s.user?.id) ?? null;

  const session = useCoAttendanceSession(conversationId);
  useAcompanhamentoWsSync(conversationId, session.coAttendanceId);

  const conversation = useConversation(conversationId);
  const { messages } = useMessages(conversationId);
  const composer = useComposerState(conversationId);
  const quality = useQualityIndicators(conversationId);
  const { data: presence } = usePresence();

  // T2-2 — ferramentas do painel direito.
  const sessionNote = useSessionNote(session.coAttendanceId);
  const coaching = useCoachingMessages(session.coAttendanceId);
  const discrepancy = useDiscrepancy(conversationId);
  const attendantUserId = conversation.data?.assignedToUser?.id ?? null;
  const parallel = useParallelConversations(attendantUserId, conversationId);

  // T2-3 — modos + ações de força.
  const caps = useCapabilities();
  const hasCapability = (code: string): boolean => caps.includes(code);
  const changeRole = useChangeRole(session.coAttendanceId);
  const force = useForceActions(conversationId);

  // Role efetivo: a sessão resolve o role uma vez; uma troca bem-sucedida (PATCH)
  // não re-emite o role pela sessão, então mantemos um override local aplicado
  // só APÓS o servidor confirmar. null = usar o role da sessão.
  const [roleOverride, setRoleOverride] = useState<CoAttendantRole | null>(null);
  const effectiveRole: CoAttendantRole | null = roleOverride ?? session.role;

  // Modais de força + justificativa de invisível.
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [playbookOpen, setPlaybookOpen] = useState(false);
  const [invisibleOpen, setInvisibleOpen] = useState(false);
  const [substituirOpen, setSubstituirOpen] = useState(false);
  const playbooks = useAvailablePlaybooks(conversationId, playbookOpen);

  const presenceFor = (userId: string): PresenceState => {
    const found = presence.find((p) => p.userId === userId);
    const status = found?.status;
    if (status === 'online' || status === 'away' || status === 'busy' || status === 'offline') {
      return status;
    }
    return 'offline';
  };
  const resolveName = (userId: string): string => {
    const found = (presence as unknown as { userId: string; name?: string }[]).find(
      (p) => p.userId === userId,
    );
    return found?.name ?? userId;
  };

  const exit = (): void => {
    session.end();
    navigate('/supervisor');
  };

  // Ref para o handler de Esc sempre ver o `exit` mais recente sem re-registrar
  // o listener a cada render (e sem `react-hooks/exhaustive-deps` no effect).
  const exitRef = useRef(exit);
  exitRef.current = exit;

  // Esc encerra o acompanhamento (não vaza sessão; o unmount também encerra).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') exitRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const monitoringMode: MonitoringMode =
    effectiveRole !== null ? ROLE_VISIBILITY[effectiveRole] : 'visible-to-attendant';

  // Troca de modo (D3). supervisor_monitor exige justificativa → abre o modal antes.
  const onSelectRole = (role: CoAttendantRole): void => {
    if (role === 'supervisor_monitor') {
      setInvisibleOpen(true);
      return;
    }
    void changeRole.changeRole(role).then((ok) => {
      // Só reflete o novo modo quando o servidor confirma (retorno do hook, não
      // o `error` em closure stale — senão a UI mentiria numa troca que falhou).
      if (ok) setRoleOverride(role);
    });
  };
  const confirmInvisible = (justification: string): void => {
    void changeRole.changeRole('supervisor_monitor', justification).then((ok) => {
      if (ok) {
        setRoleOverride('supervisor_monitor');
        setInvisibleOpen(false);
      }
    });
  };
  const confirmTakeover = (reason: string): void => {
    void force.takeover(reason);
  };
  // D9 — "Substituir atendente": executa o force-takeover e navega à Tela 3 (Intervenção)
  // carregando o `takeover` devolvido (sem GET de takeover — D1). Só navega ao sucesso real.
  const confirmSubstituir = (reason: string): void => {
    void force.takeover(reason).then((result) => {
      if (result !== null) {
        setSubstituirOpen(false);
        navigate(`/supervisor/intervencao/${conversationId}`, { state: { takeover: result } });
      }
    });
  };
  const confirmPlaybookSwitch = (newPlaybookId: string, reason: string): void => {
    void force.playbookSwitch(newPlaybookId, reason);
  };

  if (conversation.isLoading) {
    return (
      <>
        <PageHead title="Acompanhamento silencioso" />
        <p style={{ padding: 'var(--space-8)', color: 'var(--fg-3)' }}>Carregando conversa…</p>
      </>
    );
  }
  if (conversation.isError || conversation.data === undefined) {
    return (
      <>
        <PageHead title="Acompanhamento silencioso" />
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Não foi possível carregar a conversa.</span>
          </div>
        </div>
      </>
    );
  }

  const vm = adaptMirror({
    conversation: conversation.data,
    messages,
    composer: composer.data,
    presenceFor,
    resolveName,
  });
  const panelQuality = quality.data !== undefined ? adaptPanelQuality(quality.data) : null;
  const parallelVMs = parallel.data.map(adaptParallelConv);
  const coachingVMs: CoAttendantToolsCoachingMessageVM[] = coaching.messages.map((m) => ({
    id: m.id,
    from: m.fromUserId === currentUserId ? 'supervisor' : 'attendant',
    atLabel: relativeTime(m.timestamp).replace(/^há /, ''),
    text: m.text,
  }));

  const headerTone = monitoringMode.replace(/^visible-to-/, 'visible-').replace(/-to-/, '-');
  const headerIcon =
    monitoringMode === 'invisible-to-all'
      ? '👁‍🗨'
      : monitoringMode === 'visible-to-both'
        ? '📢'
        : '👁';

  // D8 — ações de força só em conversa H_c (uppercase no backend) e modo visível
  // (em invisível revelaria a intervenção). Degradado (sem sinal v1) não bloqueia.
  const isHc = conversation.data.operationMode.current === 'H_c';
  const isInvisibleMode = monitoringMode === 'invisible-to-all';
  const showForce = isHc && !isInvisibleMode;
  const canForceTakeover = showForce && hasCapability('force-takeover');
  const canForcePlaybook = showForce && hasCapability('force-playbook-switch');

  // Desfecho honesto da RESPOSTA do servidor (nunca string hardcoded do mockup).
  const takeoverOutcome =
    force.takeoverResult !== null
      ? `Takeover registrado${force.takeoverResult.status != null ? ` · status ${force.takeoverResult.status}` : ''} · a IA encerrou a condução autônoma.`
      : null;
  const playbookOutcome =
    force.playbookSwitchResult !== null
      ? `Playbook alterado${force.playbookSwitchResult.newPlaybookId != null ? ` para ${force.playbookSwitchResult.newPlaybookId}` : ''}.`
      : null;

  // D12 — banner Adversarial honesto: o backend só devolve discrepância (≠ false)
  // em bond adversarial; combinado com variant Regulated, sinaliza o contexto.
  // P1/ConflictAlert/Degradado por conversa: SEM associação observável v1 → omitidos.
  const showAdversarialBanner = variant === 'regulated' && discrepancy.indicator !== null;
  // A-30 (noturna 04/Set/2026): conversa encerrada continuava «Acompanhando…» sem sinal, mesmo
  // após reload. O status vive na conversa; a tela passa a lê-lo.
  const isClosedConversation = isTerminalStatus(conversation.data.status);

  return (
    <>
      {isClosedConversation && (
        <div
          className="banner banner--neutral"
          role="status"
          aria-label={`Conversa encerrada — ${statusLabel(conversation.data.status)}`}
        >
          <div className="banner__body">
            <span className="banner__title">
              Conversa encerrada ({statusLabel(conversation.data.status).toLowerCase()}) — o
              acompanhamento está em modo leitura; nada mais chega aqui.
            </span>
          </div>
        </div>
      )}
      <div className={`acomp-header acomp-header--${headerTone}`}>
        <span className="acomp-header__icon" aria-hidden="true">
          {headerIcon}
        </span>
        <div className="acomp-header__body">
          <div className="acomp-header__title">
            Acompanhando <b>{vm.header.attendantName}</b> em conversa com{' '}
            <b>{vm.header.customerName}</b>
          </div>
          <div className="acomp-header__sub mono">
            {monitoringMode === 'invisible-to-all' &&
              'O atendente NÃO vê que você está acompanhando · o audit log mantém registro'}
            {monitoringMode === 'visible-to-both' &&
              'O cliente foi notificado · você pode enviar mensagens ao cliente'}
            {monitoringMode === 'visible-to-attendant' && 'O atendente vê que você está aqui'}
          </div>
        </div>
        <div className="acomp-header__actions">
          {/* D3 — seletor de modo (mostra só os modos cuja cap o supervisor tem). */}
          <MonitoringModeSelector
            currentRole={effectiveRole}
            hasCapability={hasCapability}
            isChanging={changeRole.isChanging}
            onSelectRole={onSelectRole}
          />
          {/* D8 — ações de força só em H_c + modo visível, gated pelas caps de força. */}
          {canForceTakeover && (
            <button
              className="btn-ghost-mini btn-ghost-mini--primary"
              type="button"
              onClick={() => {
                setTakeoverOpen(true);
              }}
            >
              Retomar para o atendente
            </button>
          )}
          {canForcePlaybook && (
            <button
              className="btn-ghost-mini"
              type="button"
              onClick={() => {
                setPlaybookOpen(true);
              }}
            >
              Trocar playbook
            </button>
          )}
          <button className="btn-ghost-mini" type="button" onClick={exit}>
            Sair (Esc)
          </button>
          {/* D9 — "Substituir atendente" assume a conversa e abre a Tela 3 (Intervenção). */}
          {hasCapability('force-takeover') && (
            <button
              className="btn-ghost-mini"
              type="button"
              onClick={() => {
                setSubstituirOpen(true);
              }}
            >
              Substituir atendente
            </button>
          )}
        </div>
      </div>

      {changeRole.error !== null && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Não foi possível trocar o modo de acompanhamento.</span>
          </div>
        </div>
      )}

      {/* D12 — banner Adversarial honesto (bond adversarial + Regulated). */}
      {showAdversarialBanner && (
        <div className="banner banner--warn" role="status">
          <div className="banner__body">
            <span className="banner__title">
              Bond adversarial — há divergência de visibilidade entre as partes.
            </span>
            <span style={{ fontSize: 'var(--text-sm)' }}>
              A regra do silêncio se aplica: não revele o que o atendente não vê.
            </span>
          </div>
        </div>
      )}

      <PageHead title="Acompanhamento silencioso" subtitle={vm.header.attendantName} />

      <div className="tela2-body">
        <ConversationMirror
          vm={vm}
          monitoringMode={monitoringMode}
          supervisorName={supervisorName}
        />

        {/* Coluna direita — painel de ferramentas (T2-2). */}
        <CoAttendantToolsPanel
          attendantName={vm.header.attendantName}
          monitoringMode={monitoringMode}
          noteText={sessionNote.text}
          onNoteChange={sessionNote.setText}
          noteSaveState={sessionNote.saveState}
          coachingMessages={coachingVMs}
          onSendCoaching={(text) => {
            void coaching.send(text, true);
          }}
          isSendingCoaching={coaching.isSending}
          coachingSendFailed={coaching.sendError !== null}
          quality={panelQuality}
          discrepancy={discrepancy.indicator}
          showDiscrepancy={variant === 'regulated' && discrepancy.indicator !== null}
          parallelConvs={parallelVMs}
          onSwitchConversation={(id) => {
            navigate(`/supervisor/acompanhamento/${id}`);
          }}
          showForceSection={showForce}
          canForceTakeover={canForceTakeover}
          canForcePlaybook={canForcePlaybook}
          onOpenForceTakeover={() => {
            setTakeoverOpen(true);
          }}
          onOpenForcePlaybook={() => {
            setPlaybookOpen(true);
          }}
        />
      </div>

      {/* Modais T2-3 — força + justificativa de invisível. */}
      <ForceTakeoverModal
        open={takeoverOpen}
        attendantName={vm.header.attendantName}
        conversationId={conversationId}
        isPending={force.isTakeoverPending}
        failed={force.takeoverError !== null}
        outcome={takeoverOutcome}
        onClose={() => {
          setTakeoverOpen(false);
        }}
        onConfirm={confirmTakeover}
      />
      <ForceTrocaPlaybookModal
        open={playbookOpen}
        playbooks={playbooks.data}
        isLoadingPlaybooks={playbooks.isLoading}
        playbooksError={playbooks.isError}
        isPending={force.isPlaybookSwitchPending}
        failed={force.playbookSwitchError !== null}
        outcome={playbookOutcome}
        onClose={() => {
          setPlaybookOpen(false);
        }}
        onConfirm={confirmPlaybookSwitch}
      />
      <InvisibleJustificationModal
        open={invisibleOpen}
        isPending={changeRole.isChanging}
        failed={changeRole.error !== null}
        onClose={() => {
          setInvisibleOpen(false);
        }}
        onConfirm={confirmInvisible}
      />
      {/* D9 — entrada da Tela 3 (Intervenção): assume e opera publicamente. */}
      <SubstituirAtendenteModal
        open={substituirOpen}
        attendantName={vm.header.attendantName !== '—' ? vm.header.attendantName : null}
        conversationId={conversationId}
        isPending={force.isTakeoverPending}
        failed={force.takeoverError !== null}
        onClose={() => {
          setSubstituirOpen(false);
        }}
        onConfirm={confirmSubstituir}
      />
    </>
  );
}

export function AcompanhamentoPage(): React.JSX.Element {
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canObserve = useCapability('coattendance-shadow-observer');
  const { conversationId } = useParams<{ conversationId: string }>();

  if (variant === 'solo' || !canObserve) return <SoloEmpty />;
  if (conversationId === undefined) return <Landing />;
  return <Mirror conversationId={conversationId} variant={variant} />;
}
